import { cn } from "@healthos/ui";
import { Check } from "@phosphor-icons/react";
import type { ManualLogActionId, ManualLogForm } from "./manual-signal-log-model";

export type ManualLogQuickPick = {
  label: string;
  patch: Partial<ManualLogForm>;
};

const QUICK_PICKS: Record<ManualLogActionId, readonly ManualLogQuickPick[]> = {
  food: [
    { label: "Breakfast", patch: { foodMealType: "Breakfast", foodMeal: "Breakfast" } },
    { label: "Lunch", patch: { foodMealType: "Lunch", foodMeal: "Lunch" } },
    { label: "Dinner", patch: { foodMealType: "Dinner", foodMeal: "Dinner" } },
    { label: "Snack", patch: { foodMealType: "Snack", foodMeal: "Snack" } },
  ],
  hydration: [
    { label: "1 cup", patch: { hydrationAmountMl: "240", hydrationBeverage: "Water" } },
    { label: "500 ml", patch: { hydrationAmountMl: "500", hydrationBeverage: "Water" } },
    { label: "750 ml", patch: { hydrationAmountMl: "750", hydrationBeverage: "Water" } },
    { label: "1 liter", patch: { hydrationAmountMl: "1000", hydrationBeverage: "Water" } },
    { label: "Bottle", patch: { hydrationAmountMl: "500", hydrationBeverage: "Bottle" } },
    {
      label: "Electrolytes",
      patch: { hydrationAmountMl: "500", hydrationBeverage: "Electrolytes" },
    },
  ],
  sleep: [
    { label: "Slept well", patch: { sleepKind: "quality_note", sleepQuality: "good" } },
    { label: "Slept okay", patch: { sleepKind: "quality_note", sleepQuality: "ok" } },
    { label: "Poor sleep", patch: { sleepKind: "quality_note", sleepQuality: "poor" } },
    { label: "Restless", patch: { sleepKind: "quality_note", sleepQuality: "restless" } },
    { label: "Nap", patch: { sleepKind: "nap", napMinutes: "20" } },
    { label: "Short night", patch: { sleepKind: "quality_note", sleepHours: "5" } },
  ],
  // One chip, one dimension: the one its label names. Five of these wrote
  // two, so neither could be observed independently of the other.
  mood: [
    { label: "Calm", patch: { moodLabel: "Calm", stress: "1" } },
    { label: "Stressed", patch: { moodLabel: "Stressed", stress: "5" } },
    { label: "Low mood", patch: { moodLabel: "Low mood", mood: "1" } },
    { label: "Energized", patch: { moodLabel: "Energized", energy: "5" } },
    { label: "Focused", patch: { moodLabel: "Focused", focus: "5" } },
    { label: "Irritable", patch: { moodLabel: "Irritable", mood: "2" } },
  ],
  symptoms: [
    { label: "Nausea", patch: { symptomLabel: "Nausea" } },
    { label: "Headache", patch: { symptomLabel: "Headache" } },
    { label: "Fatigue", patch: { symptomLabel: "Fatigue" } },
    { label: "Pain", patch: { symptomLabel: "Pain" } },
    { label: "GI upset", patch: { symptomLabel: "GI upset" } },
    { label: "Appetite", patch: { symptomLabel: "Appetite" } },
  ],
  caffeine: [
    {
      label: "Coffee",
      patch: { intakeType: "coffee", intakeLabel: "Coffee", caffeineCups: "1", caffeineMg: "95" },
    },
    {
      label: "Tea",
      patch: { intakeType: "tea", intakeLabel: "Tea", caffeineCups: "1", caffeineMg: "45" },
    },
    {
      label: "Energy drink",
      patch: {
        intakeType: "energy_drink",
        intakeLabel: "Energy drink",
        caffeineCups: "1",
        caffeineMg: "160",
      },
    },
    {
      label: "Alcohol",
      patch: { intakeType: "alcohol", intakeLabel: "Alcohol", alcoholServings: "1" },
    },
    {
      label: "Late caffeine",
      patch: {
        intakeType: "caffeine",
        intakeLabel: "Late caffeine",
        caffeineCups: "1",
        caffeineMg: "95",
      },
    },
    {
      label: "None today",
      patch: { intakeType: "none", intakeLabel: "None today", caffeineMg: "0" },
    },
  ],
  workout: [
    { label: "Walk", patch: { workoutActivity: "Walk" } },
    { label: "Run", patch: { workoutActivity: "Run" } },
    { label: "Swim", patch: { workoutActivity: "Swim" } },
    { label: "Cycle", patch: { workoutActivity: "Cycle" } },
    { label: "Strength", patch: { workoutActivity: "Strength" } },
    { label: "Yoga", patch: { workoutActivity: "Yoga" } },
  ],
  medication: [
    { label: "Ozempic", patch: { medication: "Ozempic", doseAmount: "0.25" } },
    { label: "Wegovy", patch: { medication: "Wegovy", doseAmount: "0.25" } },
    { label: "Mounjaro", patch: { medication: "Mounjaro", doseAmount: "2.5" } },
    { label: "Zepbound", patch: { medication: "Zepbound", doseAmount: "2.5" } },
    { label: "Rybelsus", patch: { medication: "Rybelsus", doseAmount: "3" } },
    { label: "Trulicity", patch: { medication: "Trulicity", doseAmount: "0.75" } },
    { label: "Victoza", patch: { medication: "Victoza", doseAmount: "0.6" } },
    { label: "Saxenda", patch: { medication: "Saxenda", doseAmount: "0.6" } },
  ],
  vitals: [
    { label: "Weight", patch: { vitalsMetric: "weight" } },
    { label: "Blood pressure", patch: { vitalsMetric: "blood_pressure" } },
    { label: "Glucose", patch: { vitalsMetric: "glucose" } },
    { label: "Resting HR", patch: { vitalsMetric: "resting_hr" } },
    { label: "HRV", patch: { vitalsMetric: "hrv" } },
    { label: "Temperature", patch: { vitalsMetric: "temperature" } },
    { label: "HbA1c", patch: { vitalsMetric: "hba1c" } },
  ],
  // A confounder renders its own reason chips, so it has no quick-pick row.
  disruptor: [],
  // Connection renders its own type chips (ChoiceField) and LogForm excludes
  // it from the shared quick-pick row, so this is never read; kept only
  // because the Record is keyed by every ManualLogActionId.
  connection: [],
};

export function manualLogQuickPicks(actionId: ManualLogActionId): readonly ManualLogQuickPick[] {
  return QUICK_PICKS[actionId];
}

const WORKOUT_MORE_QUICK_PICKS: readonly ManualLogQuickPick[] = [
  { label: "Hiking", patch: { workoutActivity: "Hiking" } },
  { label: "Pilates", patch: { workoutActivity: "Pilates" } },
  { label: "Dance", patch: { workoutActivity: "Dance" } },
  { label: "Rowing", patch: { workoutActivity: "Rowing" } },
  { label: "Tennis", patch: { workoutActivity: "Tennis" } },
  { label: "Basketball", patch: { workoutActivity: "Basketball" } },
  { label: "HIIT", patch: { workoutActivity: "HIIT" } },
  { label: "Mobility", patch: { workoutActivity: "Mobility" } },
];

export function manualLogWorkoutMoreQuickPicks(): readonly ManualLogQuickPick[] {
  return WORKOUT_MORE_QUICK_PICKS;
}

export function ManualLogQuickPicks({
  actionId,
  label = "Quick picks",
  hideLabel = false,
  picks,
  selected,
  onSelect,
}: {
  actionId: ManualLogActionId;
  label?: string;
  hideLabel?: boolean;
  picks: readonly ManualLogQuickPick[];
  selected: string | null;
  onSelect: (pick: ManualLogQuickPick) => void;
}) {
  return (
    <fieldset>
      <legend
        className={cn(
          "mb-3 font-sans text-xs font-medium text-ink-tertiary",
          hideLabel ? "sr-only" : "",
        )}
      >
        {label}
      </legend>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
        {picks.map((pick) => {
          const active = pick.label === selected;
          return (
            <button
              key={pick.label}
              type="button"
              aria-label={pick.label}
              aria-pressed={active}
              onClick={() => onSelect(pick)}
              className={cn(
                "group relative flex min-h-[88px] flex-col items-start justify-center rounded-2xl border px-4 py-3.5 text-left transition-[background-color,border-color,color,box-shadow,transform] duration-200 ease-out active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 motion-reduce:transform-none",
                active
                  ? "border-navy bg-navy text-ink-on-brand shadow-[0_16px_30px_-22px_color-mix(in_srgb,var(--color-scrim)_80%,transparent)]"
                  : "border-border-default bg-surface-elevated text-ink-primary hover:border-border-primary hover:bg-surface-secondary/45",
              )}
            >
              <span className="pr-6 text-base font-medium leading-snug">{pick.label}</span>
              {quickPickCaption(actionId, pick.label) ? (
                <span
                  className={cn(
                    "mt-1 font-sans font-medium text-xs",
                    active ? "text-teal-200" : "text-ink-tertiary",
                  )}
                >
                  {quickPickCaption(actionId, pick.label)}
                </span>
              ) : null}
              {active ? (
                <span className="absolute right-4 top-4 flex h-4 w-4 items-center justify-center rounded-full bg-teal-300 text-ink-primary">
                  <Check size={11} weight="bold" aria-hidden="true" />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

const QUICK_PICK_CAPTIONS: Partial<Record<ManualLogActionId, Record<string, string>>> = {
  food: {
    Breakfast: "Morning",
    Lunch: "Midday",
    Dinner: "Evening",
    Snack: "Between",
  },
  hydration: {
    "1 cup": "240 ml",
    "500 ml": "Glass",
    "750 ml": "Bottle",
    "1 liter": "1000 ml",
    Bottle: "500 ml",
    Electrolytes: "With salts",
  },
  sleep: {
    "Slept well": "Restful",
    "Slept okay": "Average",
    "Poor sleep": "Low quality",
    Restless: "Interrupted",
    Nap: "Daytime rest",
    "Short night": "Under six hours",
  },
  mood: {
    Calm: "Steady",
    Stressed: "High pressure",
    "Low mood": "Flat",
    Energized: "High energy",
    Focused: "On task",
    Irritable: "Short fuse",
  },
  symptoms: {
    Nausea: "Digestive",
    Headache: "Head",
    Fatigue: "Energy",
    Pain: "Body",
    "GI upset": "Digestive",
    Appetite: "Hunger",
  },
  caffeine: {
    Coffee: "95 mg",
    Tea: "45 mg",
    "Energy drink": "160 mg",
    Alcohol: "One serving",
    "Late caffeine": "After midday",
    "None today": "No intake",
  },
  workout: {
    Walk: "Zone 1-2",
    Run: "Zone 3+",
    Swim: "Low impact",
    Cycle: "Zone 2",
    Strength: "Resistance",
    Yoga: "Mobility",
    Hiking: "Outside",
    Pilates: "Core",
    Dance: "Cardio",
    Rowing: "Full body",
    Tennis: "Racquet",
    Basketball: "Court",
    HIIT: "Intervals",
    Mobility: "Gentle",
  },
  medication: {
    Ozempic: "0.25 mg",
    Wegovy: "0.25 mg",
    Mounjaro: "2.5 mg",
    Zepbound: "2.5 mg",
    Rybelsus: "3 mg",
    Trulicity: "0.75 mg",
    Victoza: "0.6 mg",
    Saxenda: "0.6 mg",
  },
  vitals: {
    Weight: "Body mass",
    "Blood pressure": "Cuff reading",
    Glucose: "Blood sugar",
    "Resting HR": "Heart rate",
    HRV: "Variability",
    Temperature: "Body temperature",
    HbA1c: "Three months",
  },
};

function quickPickCaption(actionId: ManualLogActionId, label: string): string | undefined {
  return QUICK_PICK_CAPTIONS[actionId]?.[label];
}
